<script setup>
import { useTemplateRef, onBeforeUnmount, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTrack } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useConversationFilterContext } from './provider.js';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

import Button from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Checkbox from 'next/checkbox/Checkbox.vue';
import ConditionRow from './ConditionRow.vue';

const props = defineProps({
  isFolderView: {
    type: Boolean,
    default: false,
  },
  folderName: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['applyFilter', 'updateFolder', 'close']);
const { filterTypes } = useConversationFilterContext();

const filters = defineModel({
  type: Array,
  default: [],
});
const folderNameLocal = ref(props.folderName);

const { t } = useI18n();
const store = useStore();

const searchQuery = ref('');

// Filter categories
const filterGroups = computed(() => {
  const groups = {
    standard: { name: t('FILTER.GROUPS.STANDARD_FILTERS'), attributes: [] },
    additional: { name: t('FILTER.GROUPS.ADDITIONAL_FILTERS'), attributes: [] },
    customAttributes: { name: t('FILTER.GROUPS.CUSTOM_ATTRIBUTES'), attributes: [] },
  };

  filterTypes.value.forEach(filterType => {
    if (searchQuery.value) {
      if (!filterType.label.toLowerCase().includes(searchQuery.value.toLowerCase())) {
        return;
      }
    }
    
    if (groups[filterType.attributeModel]) {
      groups[filterType.attributeModel].attributes.push(filterType);
    } else {
      groups.standard.attributes.push(filterType);
    }
  });

  return Object.values(groups).filter(g => g.attributes.length > 0);
});

// isAllSelected checks if there's no active filter for this attribute key
const isAllSelected = (attributeKey) => {
  return !filters.value.some(f => f.attributeKey === attributeKey);
};

// Select All simply clears the filter condition for this attribute key
const selectAll = (attributeKey) => {
  filters.value = filters.value.filter(f => f.attributeKey !== attributeKey);
  // Ensure the first filter in the master list doesn't have a query operator
  if (filters.value.length > 0) {
    delete filters.value[0].queryOperator;
  }
};

const toggleComplex = (filterType, active) => {
  const attributeKey = filterType.attributeKey;
  if (active) {
    let defaultOperator = 'equal_to';
    if (filterType.filterOperators && filterType.filterOperators.length > 0) {
      defaultOperator = filterType.filterOperators[0].value;
    }
    
    let initialValues = '';
    if (filterType.inputType === 'multiSelect') {
      initialValues = [];
    } else if (['searchSelect', 'asyncSearchSelect', 'booleanSelect'].includes(filterType.inputType)) {
      initialValues = {};
    }
    
    // For contains/date, default might be different, but first operator is usually safest
    filters.value.push({
      attributeKey,
      filterOperator: defaultOperator,
      values: initialValues,
      queryOperator: filters.value.length > 0 ? 'and' : undefined,
    });
  } else {
    selectAll(attributeKey);
  }
};

const getComplexFilters = (attributeKey) => {
  return filters.value.filter(f => f.attributeKey === attributeKey);
};

const updateComplexFilter = (attributeKey, index, key, value) => {
  let count = -1;
  const globalIndex = filters.value.findIndex(f => {
    if (f.attributeKey === attributeKey) {
      count++;
      return count === index;
    }
    return false;
  });
  
  if (globalIndex > -1) {
    filters.value[globalIndex][key] = value;
  }
};

const removeCondition = (attributeKey, localIndex) => {
  let count = -1;
  const globalIndex = filters.value.findIndex(f => {
    if (f.attributeKey === attributeKey) {
      count++;
      return count === localIndex;
    }
    return false;
  });
  
  if (globalIndex > -1) {
    filters.value.splice(globalIndex, 1);
  }
  
  if (filters.value.length > 0) {
    delete filters.value[0].queryOperator;
  }
};

const resetFilter = () => {
  filters.value = [];
};

const conditionsRef = useTemplateRef('conditionsRef');

const isConditionsValid = () => {
  if (!conditionsRef.value || conditionsRef.value.length === 0) return true;
  return conditionsRef.value.every(condition => condition.validate());
};

const updateSavedCustomViews = () => {
  if (isConditionsValid()) {
    emit('updateFolder', filters.value, folderNameLocal.value);
  }
};

function validateAndSubmit() {
  if (!isConditionsValid()) {
    return;
  }

  store.dispatch(
    'setConversationFilters',
    useSnakeCase(JSON.parse(JSON.stringify(filters.value)))
  );
  emit('applyFilter', filters.value);
  useTrack(CONVERSATION_EVENTS.APPLY_FILTER, {
    appliedFilters: filters.value.map(filter => ({
      key: filter.attributeKey,
      operator: filter.filterOperator,
      queryOperator: filter.queryOperator,
    })),
  });
}

const filterModalHeaderTitle = computed(() => {
  return !props.isFolderView
    ? t('FILTER.TITLE')
    : t('FILTER.EDIT_CUSTOM_FILTER');
});

onBeforeUnmount(() => emit('close'));
const outsideClickHandler = [
  () => emit('close'),
  { ignore: ['#toggleConversationFilterButton'] },
];
</script>

<template>
  <div
    v-on-click-outside="outsideClickHandler"
    class="z-40 max-w-4xl lg:w-[700px] overflow-visible w-full border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg rounded-xl p-5 grid gap-4"
  >
    <div class="flex items-center justify-between">
      <h3 class="text-base font-medium leading-6 text-n-slate-12">
        {{ filterModalHeaderTitle }} <span v-if="filters.length > 0" class="ml-1 text-sm text-n-slate-11">({{ filters.length }})</span>
      </h3>
      <div class="w-full max-w-[240px] relative">
        <Input
          v-model="searchQuery"
          class="!mb-0 [&>input]:h-8 [&>input]:pl-8"
          placeholder="Search filters..."
        />
        <span class="absolute left-2 top-2 i-lucide-search text-n-slate-10 text-base pointer-events-none"></span>
      </div>
    </div>
    <div v-if="props.isFolderView">
      <div class="border-b border-n-weak pb-6">
        <Input
          v-model="folderNameLocal"
          :label="t('FILTER.FOLDER_LABEL')"
          :placeholder="t('FILTER.INPUT_PLACEHOLDER')"
        />
      </div>
    </div>
    
    <div class="grid gap-6 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
      <div v-for="group in filterGroups" :key="group.name" class="grid gap-4">
        <h4 class="text-sm font-bold text-n-slate-11 tracking-widest uppercase border-b border-n-weak pb-2">{{ group.name }}</h4>
        
        <div class="grid grid-cols-1 md:grid-cols-4 gap-x-4 gap-y-4">
          <div v-for="filterType in group.attributes" :key="filterType.attributeKey" class="flex flex-col gap-1.5">
            
            <h5 class="text-sm font-semibold text-n-slate-12 mb-1">{{ filterType.label }}</h5>
            <div class="flex flex-col gap-2">
              <label class="flex items-center gap-1.5 cursor-pointer select-none w-fit">
                <Checkbox 
                  :model-value="isAllSelected(filterType.attributeKey)"
                  @change="$event.target.checked ? selectAll(filterType.attributeKey) : toggleComplex(filterType, true)"
                />
                <span class="text-sm text-n-slate-11" :class="{ 'font-medium text-n-slate-12': isAllSelected(filterType.attributeKey) }">All</span>
              </label>
              
              <div v-if="!isAllSelected(filterType.attributeKey)" class="pl-5 grid gap-2 mt-1">
                <ConditionRow
                  v-for="(filter, index) in getComplexFilters(filterType.attributeKey)"
                  :key="`${filter.attributeKey}-${index}`"
                  ref="conditionsRef"
                  :attribute-key="filter.attributeKey"
                  :filter-operator="filter.filterOperator"
                  :values="filter.values"
                  :query-operator="filter.queryOperator"
                  :filter-types="filterTypes"
                  :hide-attribute="true"
                  :hide-operator="true"
                  :show-query-operator="index > 0"
                  @update:attribute-key="v => updateComplexFilter(filterType.attributeKey, index, 'attributeKey', v)"
                  @update:filter-operator="v => updateComplexFilter(filterType.attributeKey, index, 'filterOperator', v)"
                  @update:values="v => updateComplexFilter(filterType.attributeKey, index, 'values', v)"
                  @update:query-operator="v => updateComplexFilter(filterType.attributeKey, index, 'queryOperator', v)"
                  @remove="removeCondition(filterType.attributeKey, index)"
                />
              </div>
            </div>
            
          </div>
        </div>
      </div>
      
      <div v-if="filterGroups.length === 0" class="text-sm text-n-slate-11 text-center py-8">
        No filters match your search.
      </div>
    </div>

    <div class="flex gap-2 justify-between border-t border-n-weak pt-4">
      <Button sm faded slate @click="resetFilter" :disabled="filters.length === 0">
        {{ t('FILTER.CLEAR_BUTTON_LABEL') }}
      </Button>
      <div class="flex gap-2">
        <Button
          v-if="isFolderView"
          sm
          solid
          blue
          :disabled="!folderNameLocal"
          @click="updateSavedCustomViews"
        >
          {{ t('FILTER.UPDATE_BUTTON_LABEL') }}
        </Button>
        <Button v-else sm solid blue @click="validateAndSubmit">
          {{ t('FILTER.SUBMIT_BUTTON_LABEL') }}
        </Button>
      </div>
    </div>
  </div>
</template>
